// File: components/CustomAlert.js
import React from 'react';
// Import các component Material-UI từ @material-ui/core (cho v4)
import { Box, Typography, IconButton, Paper } from '@material-ui/core';

// Import các Icons từ @material-ui/icons (cho v4)
import {
    CheckCircleOutline,
    ErrorOutline,
    InfoOutlined,
    WarningOutlined,
    Close,
} from '@material-ui/icons';

// Import makeStyles từ @material-ui/core/styles (cho v4)
import { makeStyles } from '@material-ui/core/styles';

// Định nghĩa styles bằng makeStyles
const useStyles = makeStyles((theme) => ({
    alertContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.5), // Padding
        borderRadius: theme.shape.borderRadius, // Bo tròn góc mặc định của theme
        width: '100%',
        boxSizing: 'border-box',
        // Màu nền và màu chữ sẽ được áp dụng động
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        marginRight: theme.spacing(1.5),
    },
    icon: {
        fontSize: 20,
        // Màu icon sẽ được áp dụng động
    },
    message: {
        flexGrow: 1,
        // Màu chữ sẽ được áp dụng động
    },
    closeButton: {
        marginLeft: theme.spacing(1.5),
        '&:hover': {
            opacity: 0.8,
        },
    },
    // Các lớp màu sắc theo severity
    success: {
        backgroundColor: '#e6ffed', // Màu nền xanh lá nhạt
        color: '#1e4620', // Màu chữ xanh lá đậm
    },
    error: {
        backgroundColor: '#ffebee', // Màu nền đỏ nhạt
        color: '#5c171e', // Màu chữ đỏ đậm
    },
    warning: {
        backgroundColor: '#fff8e1', // Màu nền vàng nhạt
        color: '#663c00', // Màu chữ vàng đậm
    },
    info: {
        backgroundColor: '#e3f2fd', // Màu nền xanh dương nhạt
        color: '#0a3666', // Màu chữ xanh dương đậm
    },
}));

/**
 * Custom Alert component thay thế cho @mui/lab/Alert.
 * Chuyển đổi sang makeStyles cho Material-UI v4.
 *
 * @param {object} props - Các props của component.
 * @param {string} props.message - Nội dung thông báo.
 * @param {'success' | 'error' | 'info' | 'warning'} [props.severity='info'] - Mức độ nghiêm trọng của thông báo.
 * @param {function} [props.onClose] - Hàm được gọi khi nút đóng được nhấn.
 * @param {string} [props.className] - Class tùy chỉnh để ghi đè hoặc mở rộng styles.
 */
function CustomAlert({ message, severity = 'info', onClose, className }) {
    const classes = useStyles();

    // Xác định Icon Component dựa trên severity
    let IconComponent;
    switch (severity) {
        case 'success':
            IconComponent = CheckCircleOutline;
            break;
        case 'error':
            IconComponent = ErrorOutline;
            break;
        case 'warning':
            IconComponent = WarningOutlined;
            break;
        case 'info':
        default:
            IconComponent = InfoOutlined;
            break;
    }

    // Kết hợp các class dựa trên severity
    const alertClasses = `${classes.alertContainer} ${classes[severity]}`;

    return (
        <Paper
            elevation={2}
            // Áp dụng các classes đã định nghĩa
            className={`${alertClasses} ${className || ''}`} // Thêm className từ props nếu có
        >
            {/* Icon */}
            {IconComponent && (
                <Box className={classes.iconWrapper}>
                    <IconComponent className={classes.icon} />
                </Box>
            )}

            {/* Nội dung thông báo */}
            <Typography variant="body2" className={classes.message}>
                {message}
            </Typography>

            {/* Nút đóng */}
            {onClose && (
                <IconButton
                    aria-label="close"
                    color="inherit" // Kế thừa màu chữ từ parent
                    size="small"
                    onClick={onClose}
                    className={classes.closeButton}
                >
                    <Close fontSize="inherit" />
                </IconButton>
            )}
        </Paper>
    );
}

export default CustomAlert;